import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

import { General, MediaDevices } from 'icons/untitledui';

import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import useNoteAttachmentPicker from 'shiftNotes/hooks/useNoteAttachmentPicker';
import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'shiftNotes/styles/NoteCaptureWriting.style';
import NoteCaptureActionCard from './NoteCaptureActionCard';
import NoteCaptureAttachmentsPreview from './NoteCaptureAttachmentsPreview';

export interface NoteCaptureWritingProps {
  onCancel: () => void;
  onSubmit: (text: string) => void;
  onSwitchToVoice?: () => void;
  initialValue?: string;
  inline?: boolean;
}

export default function NoteCaptureWriting({
  onCancel,
  onSubmit,
  onSwitchToVoice,
  initialValue = '',
  inline = false,
}: NoteCaptureWritingProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const { attachments, clearAttachments, isResolveMode } =
    useShiftNoteCapture();
  const [text, setText] = useState(initialValue);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSwitchToVoice, setPendingSwitchToVoice] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const isTextEmpty = text.trim().length === 0;
  const isEmpty = isTextEmpty && attachments.length === 0;
  const placeholder = isResolveMode
    ? t('noteCapture.resolveHint')
    : t('noteCaptureWriting.placeholder');

  const { attachmentError, handleRemoveAttachment, controls } =
    useNoteAttachmentPicker({ fromState: 'text' });

  useEffect(() => {
    textAreaRef.current?.focus();
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  };

  const handleCancelClick = () => {
    if (isEmpty) {
      onCancel();
      return;
    }
    setPendingSwitchToVoice(false);
    setShowConfirmation(true);
  };

  const handleKeepWriting = () => {
    setPendingSwitchToVoice(false);
    setShowConfirmation(false);
  };

  const handleDiscard = () => {
    setShowConfirmation(false);
    if (pendingSwitchToVoice && onSwitchToVoice) {
      setPendingSwitchToVoice(false);
      onSwitchToVoice();
      return;
    }
    clearAttachments();
    onCancel();
  };

  const handleSwitchToVoice = () => {
    if (!onSwitchToVoice) return;
    if (isTextEmpty) {
      onSwitchToVoice();
      return;
    }
    setPendingSwitchToVoice(true);
    setShowConfirmation(true);
  };

  const handleSubmit = () => {
    if (isEmpty) return;
    onSubmit(text.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (showConfirmation) {
    return (
      <NoteCaptureActionCard
        title={t('noteCaptureWriting.cancelConfirm.title')}
        description={t('noteCaptureWriting.cancelConfirm.message')}
        secondaryLabel={t('noteCaptureWriting.cancelConfirm.keepWriting')}
        onSecondaryClick={handleKeepWriting}
        primaryLabel={t('noteCaptureWriting.cancelConfirm.discard')}
        onPrimaryClick={handleDiscard}
        inline={inline}
      />
    );
  }

  return (
    <Styled.Card $inline={inline}>
      <Styled.TopBar>
        <Buttons.SecondaryLightIcon
          $size="medium"
          onClick={handleCancelClick}
          aria-label={t('noteCaptureWriting.cancel')}
          data-testid="note-capture-cancel-button"
        >
          <General.XClose />
        </Buttons.SecondaryLightIcon>
      </Styled.TopBar>
      <Styled.TextInputSection>
        <Styled.TextArea
          ref={textAreaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          data-testid="note-capture-text-input"
        />
      </Styled.TextInputSection>
      {attachments.length > 0 && (
        <NoteCaptureAttachmentsPreview
          files={attachments}
          onRemove={handleRemoveAttachment}
        />
      )}
      {attachmentError && (
        <Styled.AttachmentError role="alert">
          {t(attachmentError.key, attachmentError.params)}
        </Styled.AttachmentError>
      )}
      <Styled.ControlsSection>
        <Styled.ControlsLeft>
          {controls}
          {onSwitchToVoice && (
            <Buttons.SecondaryLightIcon
              $size="medium"
              onClick={handleSwitchToVoice}
              aria-label={t('noteCaptureModeToggle.switchToVoice')}
              data-testid="note-capture-mode-voice"
            >
              <MediaDevices.Microphone01 />
            </Buttons.SecondaryLightIcon>
          )}
        </Styled.ControlsLeft>
        <Tooltip title={t('noteCaptureWriting.submitShortcut')} arrow>
          <span>
            <Buttons.PrimaryIcon
              $size="medium"
              onClick={handleSubmit}
              disabled={isEmpty}
              aria-label={t('noteCaptureWriting.confirm')}
              aria-disabled={isEmpty}
              data-testid="note-capture-text-submit"
            >
              <General.Check />
            </Buttons.PrimaryIcon>
          </span>
        </Tooltip>
      </Styled.ControlsSection>
    </Styled.Card>
  );
}
